/// <reference types="google.accounts" />
/* global google -- defined by Google Identity Services once its script loads (typed by @types/google.accounts) */
import type { GoogleButtonRenderOptions } from '@talbenmoshe/trip-os-design-system';
import type { IGoogleSignIn } from '@talbenmoshe/trip-os-react-model';

/** Fills a container with Google's own "Continue with Google" button; Google requires its own branded button. */
export interface IGoogleButtonRenderer {
  renderButton(container: HTMLElement, options: GoogleButtonRenderOptions): void;
}

const GOOGLE_IDENTITY_SERVICES_SCRIPT = 'https://accounts.google.com/gsi/client';
/** Google's `renderButton` accepts at most 400 px; a wider slot gets a 400 px button (tracker Phase 09 note). */
const GOOGLE_BUTTON_MAXIMUM_WIDTH = 400;

/**
 * The browser's Google sign-in for the login site, through Google Identity Services — the only code in the app that
 * touches the `google` global; the client sees it as `IGoogleSignIn`, the sign-in screen as `IGoogleButtonRenderer`.
 *
 * Popup mode (with FedCM where the browser has it) hands the ID token back to this page; the login host's shell sends
 * `Cross-Origin-Opener-Policy: same-origin-allow-popups` so the popup can.
 */
export class GoogleIdentityServicesSignIn implements IGoogleSignIn, IGoogleButtonRenderer {
  private scriptLoad: Promise<void> | undefined;

  constructor(private readonly document: Document) {}

  async prepare(clientId: string, onIdToken: (idToken: string) => void): Promise<void> {
    await this.loadScript();
    google.accounts.id.initialize({
      // The option names are Google's.
      /* eslint-disable camelcase */
      client_id: clientId,
      callback: response => onIdToken(response.credential),
      ux_mode: 'popup',
      use_fedcm_for_button: true
      /* eslint-enable camelcase */
    });
  }

  renderButton(container: HTMLElement, options: GoogleButtonRenderOptions): void {
    google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: options.theme === 'dark' ? 'filled_black' : 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      // eslint-disable-next-line camelcase -- Google's option name
      logo_alignment: 'left',
      width: Math.min(options.width, GOOGLE_BUTTON_MAXIMUM_WIDTH)
    });
  }

  /** Injects Google's script once; a failed load is removed and forgotten, so a later `prepare` injects a fresh one. */
  private loadScript(): Promise<void> {
    this.scriptLoad ??= new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = GOOGLE_IDENTITY_SERVICES_SCRIPT;
      script.async = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        script.remove();
        this.scriptLoad = undefined;
        reject(new Error('Google Identity Services did not load'));
      });
      this.document.head.appendChild(script);
    });

    return this.scriptLoad;
  }
}
